import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

export const useMemoryHandler = () => {
  const { user, updateMemory } = useAuth();
  const toast = useToast();
  const [localMemoryArray, setLocalMemoryArray] = useState<string[]>(user?.preferences || []);
  const [originalMemoryArray, setOriginalMemoryArray] = useState<string[]>(user?.preferences || []);
  const [expandedMemoryIndex, setExpandedMemoryIndex] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (user?.preferences && Array.isArray(user.preferences)) {
      setLocalMemoryArray(user.preferences);
      setOriginalMemoryArray(user.preferences);
    } else {
      setLocalMemoryArray([]);
      setOriginalMemoryArray([]);
    }
  }, [user]);

  const handleMemoryChange = (index: number, value: string) => {
    const updatedMemory = [...localMemoryArray];
    updatedMemory[index] = value;
    setLocalMemoryArray(updatedMemory);
    setHasChanges(JSON.stringify(updatedMemory) !== JSON.stringify(originalMemoryArray));
  };

  const handleMemorySubmit = async () => {
    setIsLoading(true);
    try {
      const cleanedMemoryArray = localMemoryArray.filter(memory => memory && memory.trim());
      
      await updateMemory(cleanedMemoryArray);
      setLocalMemoryArray(cleanedMemoryArray);
      setOriginalMemoryArray(cleanedMemoryArray);
      setHasChanges(false);
      toast('Personalization updated successfully', 'success');
    } catch (error) {
      toast('Failed to update personalization', 'error');
      setLocalMemoryArray([...originalMemoryArray]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMemory = () => {
    const updatedMemory = [...localMemoryArray, ''];
    setLocalMemoryArray(updatedMemory);
    setHasChanges(JSON.stringify(updatedMemory) !== JSON.stringify(originalMemoryArray));
  };

  const handleRemoveMemory = (index: number) => {
    const updatedMemory = localMemoryArray.filter((_, i) => i !== index);
    setLocalMemoryArray(updatedMemory);
    setHasChanges(JSON.stringify(updatedMemory) !== JSON.stringify(originalMemoryArray));
  };

  return {
    localMemoryArray,
    expandedMemoryIndex,
    setExpandedMemoryIndex,
    hasChanges,
    isLoading,
    handleMemoryChange,
    handleMemorySubmit,
    handleAddMemory,
    handleRemoveMemory
  };
}; 